import React, { PropTypes, PureComponent } from 'react';
import { Link } from 'react-router';
import { graphql, compose } from 'react-apollo';
import enhanceWithClickOutside from 'react-click-outside';

import authService from '../../../state/services/auth';
import { GQLQueryPropType } from '../../../proptypes';
import {
  trackExternalBookingSaved, trackExternalBookingUnsaved,
} from '../../../utils/tracking';
import {
  UserQueryPublicSavedExternalBookings,
  SaveExternalBookingMutation,
  UnsaveExternalBookingMutation,
  getSaveExternalBookingMutationOptimisticResponse,
  getUnsaveExternalBookingMutationOptimisticResponse,
} from '../../../state/services/gql/user';
import {
  arrowBox, container, dropdown, dropdownTitle, dropdownDescription, dropdownRaces,
  dropdownRace, dropdownRaceSaved, hoverPlaceholder,
  icon, iconSaved, iconText, iconTextSaved,
} from './styles.module.css';

class ExternalBookingButton extends PureComponent {
  state = {
    expanded: false,
    loggedIn: authService.loggedIn(),
  }

  componentDidMount() {
    // see the 'Not working on iOS' section on:
    // https://www.npmjs.com/package/react-click-outside
    if ('ontouchstart' in document.documentElement) {
      document.body.style.cursor = 'pointer';
    }
  }

  handleClickOutside() {
    this.setState({ expanded: false });
  }

  saveExternalBooking(eventId, raceId) {
    this.props.onSave({
      variables: {
        eventId,
        raceId,
      },
      optimisticResponse: getSaveExternalBookingMutationOptimisticResponse(
        eventId, raceId, this.props.data.profile,
      ),
    }).then(() => {
      trackExternalBookingSaved({ id: eventId });
    });
  }

  unsaveExternalBooking(eventId, raceId) {
    this.props.onUnsave({
      variables: {
        eventId,
        raceId,
      },
      optimisticResponse: getUnsaveExternalBookingMutationOptimisticResponse(
        eventId, raceId, this.props.data.profile,
      ),
    }).then(() => {
      trackExternalBookingUnsaved({ id: eventId });
    });
  }

  render() {
    const eventIsSaved =
      this.props.data && this.props.data.profile &&
      this.props.data.profile.savedExternalBookings &&
      this.props.data.profile.savedExternalBookings.find(
        eb =>
          eb.race && eb.race.event &&
          eb.race.event.id === this.props.eventId.toString(),
      );
    const eventIsBooked =
      this.props.data && this.props.data.profile &&
        this.props.data.profile.bookings &&
        this.props.data.profile.bookings.find(
          b =>
            b.ticket && b.ticket.race && b.ticket.race.event &&
            b.ticket.race.event.id === this.props.eventId.toString() &&
            b.isPaymentAuthorized,
        );
    return (/* eslint-disable jsx-a11y/no-static-element-interactions */
      <div
        className={this.props.className}
        style={{ position: 'relative' }}
        onClick={() => this.setState({ expanded: !this.state.expanded })}
      >
        <div className={container}>
          <div
            className={[icon].concat((eventIsSaved || eventIsBooked) ? iconSaved : []).join(' ')}
          />
          <div className={[iconText].concat((eventIsSaved || eventIsBooked) ? iconTextSaved : []).join(' ')}>
            {'Going'}
          </div>
        </div>
        <div className={hoverPlaceholder} />
        {this.state.expanded &&
        <div className={dropdown}>
          <div className={arrowBox}>
            <p className={dropdownTitle}>
              {eventIsBooked
                ? 'Congratulations!'
                : 'Which race?'
              }
            </p>
            <p className={dropdownDescription}>
              {eventIsBooked
                ? 'You\'re already booked in'
                : 'Select a race to add it to your profile'
              }
            </p>
            <div className={dropdownRaces}>
              {this.props.races && this.props.races.races
                .map((r) => {
                  const raceIsSaved =
                    this.props.data && this.props.data.profile &&
                    this.props.data.profile.savedExternalBookings &&
                    this.props.data.profile.savedExternalBookings.find(
                      eb =>
                        eb.race && eb.race.event &&
                        eb.race.event.id === this.props.eventId.toString() &&
                        eb.race.id === r.id,
                    );
                  const raceIsBooked =
                    this.props.data && this.props.data.profile &&
                      this.props.data.profile.bookings &&
                      this.props.data.profile.bookings.find(
                        b =>
                          b.ticket && b.ticket.race && b.ticket.race.event &&
                          b.ticket.race.event.id === this.props.eventId.toString() &&
                          b.ticket.race.id === r.id &&
                          b.isPaymentAuthorized,
                      );
                  if (this.state.loggedIn) {
                    let onClick = () => {};
                    if (!raceIsBooked) {
                      onClick = () => (raceIsSaved
                        ? this.unsaveExternalBooking(
                          this.props.eventId.toString(),
                          r.id,
                          this.props.data.profile.id,
                        ) : this.saveExternalBooking(
                          this.props.eventId.toString(),
                          r.id,
                          this.props.data.profile.id,
                        ));
                    }
                    return (
                      <div
                        className={[dropdownRace].concat((raceIsSaved || raceIsBooked)
                          ? dropdownRaceSaved
                          : []).join(' ')
                        }
                        onClick={onClick}
                        key={`externalbooking-race-${r.id}`}
                      >
                        {r.title}
                      </div>
                    );
                  }
                  return (
                    <Link
                      to={`/save-externalbooking/${this.props.eventId}/${r.id}`}
                      className={[dropdownRace].concat((raceIsSaved || raceIsBooked)
                        ? dropdownRaceSaved
                        : []).join(' ')
                      }
                      key={`externalbooking-race-${r.id}`}
                    >
                      {r.title}
                    </Link>
                  );
                })
              }
            </div>
          </div>
        </div>}
      </div>
    );
  }
}

ExternalBookingButton.propTypes = {
  /**
   * The name of the CSS class to apply to this component
   */
  className: PropTypes.string,
  /**
   * Data object returned from GraphQL query.
   */
  data: GQLQueryPropType,
  /**
   * The id of the event
   */
  eventId: PropTypes.number.isRequired,
  /**
   * Callback function when user clicks on Save Event button.
   */
  onSave: PropTypes.func,
  /**
   * Array of race objects.
   */
  races: PropTypes.shape(),
  /**
   * Callback function when user unsaves event.
   */
  onUnsave: PropTypes.func,
};

ExternalBookingButton.defaultProps = {
  className: '',
  data: {},
  onSave: () => {},
  onUnsave: () => {},
  races: {},
};

// Shorthand object definition, maps action creator to dispatch
export default (props) => {
  const ExternalBookingButtonWithGraphQL = compose(
    graphql(SaveExternalBookingMutation, { name: 'onSave' }),
    graphql(UnsaveExternalBookingMutation, { name: 'onUnsave' }),
    graphql(
      UserQueryPublicSavedExternalBookings,
      {
        options: { variables: { id: authService.getUserCookie() } },
        skip: !authService.getUserCookie(),
      },
    ),
  )(enhanceWithClickOutside(ExternalBookingButton));

  return <ExternalBookingButtonWithGraphQL {...props} />;
};



// WEBPACK FOOTER //
// ./app/src/components/EventHeader/ExternalBookingButton/index.jsx